import {normalizePhone} from "../user/ui/utils";
import {
    FormStep,
    LoginVerification,
    ManualLoginRequest,
    RegistrationRequest,
    UserAttributeDefinition,
    UserInput,
    UserLoginPrincipal,
    UserPrimaryField,
    UserVerificationRequirements
} from "../http/protocol";
import {useDispatch, useSelector} from "react-redux";
import * as React from "react";
import {useState} from "react";

import {
    clearManualApprovalRequired,
    login,
    manualApprovalRequired,
    registerAccount,
    UserActionTypes
} from "../user/UserActions";
import {AppState} from "../RootReducer";
import {findStepIndexFromField, toOptionsArray} from "./user-utils";
import {FormStepResult, SubmissionError, UserDetailsFormStep} from "./UserDetailsFormStep";
import {t} from "../i18n";

type UserAttributes = { [index: string]: UserAttributeDefinition };


const toInitialState = (s : FormStep, userAttributes : UserAttributes) => {
    let initial : RegistrationRequest = {};
    (s.groups ?? []).flatMap(g => g.inputs).forEach((input) => {
        if(input?.userAttributeKey && userAttributes[input.userAttributeKey]) {
            let options = userAttributes[input.userAttributeKey].options;
            if(options && options.trim().length > 0) {
                let opts = toOptionsArray(options);
                if(opts.length > 0) {
                    let attributes = initial?.attributes ?? {};
                    attributes[input.userAttributeKey] = opts[0];
                    initial.attributes = attributes;
                }
            }
        }
    })
    return initial;
}
export const UserDetailsForm = (props : {  loginPrincipals : UserLoginPrincipal[], formSteps? : FormStep[], userAttributes? :UserAttributes, verification? : UserVerificationRequirements}) => {
    const userAttributes : UserAttributes = props.userAttributes ?? {};
    const formSteps = props.formSteps;

    const dispatch = useDispatch();
    const [stepIndex, setStepIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<SubmissionError>({});
    const initial = (formSteps ?? []).map(s => {
        return { valid : false, data : toInitialState(s, userAttributes)};
    });
    const [stepState, setStepState] = useState<FormStepResult[]>(initial);
    const [principal, setPrincipal] = useState(props.loginPrincipals[0])
    const settings = useSelector((app : AppState) => app.settings.settings);
    const appMode = useSelector((app : AppState) => app.user.appMode)

    const submit = async (stepState : FormStepResult[]) => {
        setSubmitting(true);

        let data : RegistrationRequest & { termsAccepted? : boolean } = {};
        stepState.forEach(d => {
            data = {...data,...d.data};
        })
        try {
            data = {...data, principal : principal};
            if(data.phoneNumber && data.phoneNumber.trim().length > 0) {
                data = {...data, phoneNumber : normalizePhone(data.phoneNumber)}
            }
            if(data.phoneNumber == "") {
                delete data["phoneNumber"];
            }
            if(data.email == "") {
                delete data["email"];
            }
            if(data.termsAccepted) {
                delete data["termsAccepted"];
            }
            data.attributes = {...data.attributes};
            let keys = Object.keys(data.attributes);
            keys.forEach(key => {
                if(data.attributes?.[key] === "") {
                    delete data.attributes[key];
                } else if(data.attributes?.[key]) {
                    data.attributes[key] = data.attributes[key] + "";
                }
            })
            let response = await registerAccount(data, settings);
            dispatch({ type : UserActionTypes.REGISTER_RESPONSE, response: response});
            // let homescreen:boolean = (window.matchMedia('(display-mode: fullscreen)').matches || window.matchMedia('(display-mode: standalone)').matches);

            if(response.userInfo?.manualApprovalRequired === true){
              dispatch(manualApprovalRequired())
            } else if(settings?.requireUserChannelVerification === false) {
                let loginRequest: ManualLoginRequest = {email: data.email, phoneNumber: data.phoneNumber, password: data.password, /*homescreen: homescreen,*/ appMode: appMode};
                dispatch(login(loginRequest) as any);
            } else {
                let loginRequest: ManualLoginRequest = {
                    email: data.email,
                    phoneNumber: data.phoneNumber,
                    password: data.password,
                    /*homescreen:homescreen,*/
                    appMode: appMode,
                    verification : (principal == UserLoginPrincipal.EMAIL) ? LoginVerification.EMAIL : LoginVerification.PHONE
                };
                dispatch({ type : UserActionTypes.VERIFY_EMAIL_RESPONSE, request: loginRequest });

                //skip settings submitting to false to prevent submit button to appear as non-loading while logging in / verify email redirect is in process
            }
        } catch(e){
            setSubmitting(false)
            setSubmitError({ message : e.message, field : e.field});
            let errorInStep = findStepIndexFromField(principal, e.field, formSteps);
            if(errorInStep != -1) {
                setStepIndex(errorInStep);
            }
        }

    }

    const next = (fsr : FormStepResult, goToIndex?: number) => {
        let update = stepState.concat();
        update[stepIndex] = fsr;
        setStepState(update);
        if(typeof(goToIndex) != "undefined") {
            setStepIndex(goToIndex);
        } else if(stepIndex < nrOfSteps - 1) {
            setStepIndex(stepIndex + 1);
        } else {
            setSubmitError({});
            if(update.filter(s => s.valid === false).length == 0) {
                return submit(update);
            } else {
                setSubmitting(false);
            }
        }
    }

    let allValid = stepState.filter((s, index) => {
        if(index == stepState.length - 1) {
            return false;
        }
        return !s.valid;
    }).length == 0;

    const getAttribute = (attributeKey? : string) => {
        if(typeof(attributeKey) == "undefined") {
            return;
        }
        return userAttributes[attributeKey];
    }
    const isInputValid = (input : UserInput) => {
        if(typeof(input.userAttributeKey) != "undefined" && !getAttribute(input.userAttributeKey)) {
            return false;
        }
        return true;
    }
    let nrOfSteps = formSteps?.length ?? 0;

    let excludeEmail = (i : UserInput) => !(principal == UserLoginPrincipal.EMAIL && i.field == UserPrimaryField.email);
    let excludePhoneNumber = (i : UserInput) => !(principal == UserLoginPrincipal.PHONE_NUMBER && i.field == UserPrimaryField.phoneNumber);

    const inputFilter = (i : UserInput) => {
        return isInputValid(i) && excludeEmail(i) && excludePhoneNumber(i);
    }

    if(!props.loginPrincipals || props.loginPrincipals.length == 0) {
        return (
            <div>
                <div className={"alert alert-danger"}>
                    {t("user.registration-disabled")}
                </div>
            </div>
        )
    }

    return (
        <>
            {formSteps && formSteps[stepIndex] && (
                <UserDetailsFormStep step={formSteps[stepIndex] ?? {}}
                                     loginPrincipals={props.loginPrincipals}
                                     principal={principal}
                                     setPrincipal={setPrincipal}
                                     inputFilter={inputFilter}
                                     stepIndex={stepIndex}
                                     nrOfSteps={nrOfSteps}
                                     stepState={stepState?.[stepIndex].data ?? {}}
                                     next={next}
                                     allValid={allValid}
                                     submitError={submitError}
                                     submitting={submitting}
                                     getAttribute={getAttribute} />
            )}


        </>
    )

}

